/** COCO-17 keypoint indices — keep in sync with server/vision/angle_calc.py */
export const KP = {
  LEFT_SHOULDER: 5,
  RIGHT_SHOULDER: 6,
  LEFT_ELBOW: 7,
  RIGHT_ELBOW: 8,
  LEFT_HIP: 11,
  RIGHT_HIP: 12,
  LEFT_KNEE: 13,
  RIGHT_KNEE: 14,
  LEFT_ANKLE: 15,
  RIGHT_ANKLE: 16,
} as const;

export const ANGLE_LABELS: Record<string, string> = {
  left_knee: '左膝',
  right_knee: '右膝',
  left_hip: '左髋',
  right_hip: '右髋',
  left_elbow: '左肘',
  right_elbow: '右肘',
  spine: '脊柱',
  shoulder_tilt: '肩倾',
  left_ankle_flex: '左踝',
  right_ankle_flex: '右踝',
  left_foot_align: '左足',
  right_foot_align: '右足',
};

/** Vertex keypoint (or composite anchor) for each angle measurement */
const ANGLE_ANCHOR: Record<string, number | 'mid_shoulder' | 'mid_spine'> = {
  left_knee: KP.LEFT_KNEE,
  right_knee: KP.RIGHT_KNEE,
  left_hip: KP.LEFT_HIP,
  right_hip: KP.RIGHT_HIP,
  left_elbow: KP.LEFT_ELBOW,
  right_elbow: KP.RIGHT_ELBOW,
  left_ankle_flex: KP.LEFT_ANKLE,
  right_ankle_flex: KP.RIGHT_ANKLE,
  left_foot_align: KP.LEFT_ANKLE,
  right_foot_align: KP.RIGHT_ANKLE,
  spine: 'mid_spine',
  shoulder_tilt: 'mid_shoulder',
};

/** Match skeleton visibility threshold in PoseCanvas */
export const KP_CONF_THRESHOLD = 0.15;

type Kp = [number, number, number];

function validKp(kp: Kp | undefined): kp is Kp {
  return !!kp && kp[2] >= KP_CONF_THRESHOLD;
}

function validIdx(keypoints: Kp[], idx: number): boolean {
  return idx < keypoints.length && validKp(keypoints[idx]);
}

function calculateAngle(a: Kp, b: Kp, c: Kp): number {
  const bax = a[0] - b[0], bay = a[1] - b[1];
  const bcx = c[0] - b[0], bcy = c[1] - b[1];
  const normBa = Math.hypot(bax, bay);
  const normBc = Math.hypot(bcx, bcy);
  if (normBa === 0 || normBc === 0) return 0;
  const cos = Math.max(-1, Math.min(1, (bax * bcx + bay * bcy) / (normBa * normBc)));
  return (Math.acos(cos) * 180) / Math.PI;
}

function calculateVerticalAngle(top: [number, number], bottom: [number, number]): number {
  const dy = top[1] - bottom[1];
  const dx = top[0] - bottom[0];
  return (Math.atan2(Math.abs(dx), Math.abs(dy) + 1e-6) * 180) / Math.PI;
}

/** Client-side angle calc — same joints as server, lower confidence threshold */
export function calculateBodyAngles(keypoints: Kp[]): Record<string, number> {
  const angles: Record<string, number> = {};

  if (validIdx(keypoints, KP.LEFT_HIP) && validIdx(keypoints, KP.LEFT_KNEE) && validIdx(keypoints, KP.LEFT_ANKLE)) {
    angles.left_knee = calculateAngle(keypoints[KP.LEFT_HIP], keypoints[KP.LEFT_KNEE], keypoints[KP.LEFT_ANKLE]);
  }
  if (validIdx(keypoints, KP.RIGHT_HIP) && validIdx(keypoints, KP.RIGHT_KNEE) && validIdx(keypoints, KP.RIGHT_ANKLE)) {
    angles.right_knee = calculateAngle(keypoints[KP.RIGHT_HIP], keypoints[KP.RIGHT_KNEE], keypoints[KP.RIGHT_ANKLE]);
  }

  if (validIdx(keypoints, KP.LEFT_SHOULDER) && validIdx(keypoints, KP.LEFT_HIP) && validIdx(keypoints, KP.LEFT_KNEE)) {
    angles.left_hip = calculateAngle(keypoints[KP.LEFT_SHOULDER], keypoints[KP.LEFT_HIP], keypoints[KP.LEFT_KNEE]);
  }
  if (validIdx(keypoints, KP.RIGHT_SHOULDER) && validIdx(keypoints, KP.RIGHT_HIP) && validIdx(keypoints, KP.RIGHT_KNEE)) {
    angles.right_hip = calculateAngle(keypoints[KP.RIGHT_SHOULDER], keypoints[KP.RIGHT_HIP], keypoints[KP.RIGHT_KNEE]);
  }

  if (validIdx(keypoints, KP.LEFT_SHOULDER) && validIdx(keypoints, KP.LEFT_ELBOW) && validIdx(keypoints, 9)) {
    angles.left_elbow = calculateAngle(keypoints[KP.LEFT_SHOULDER], keypoints[KP.LEFT_ELBOW], keypoints[9]);
  }
  if (validIdx(keypoints, KP.RIGHT_SHOULDER) && validIdx(keypoints, KP.RIGHT_ELBOW) && validIdx(keypoints, 10)) {
    angles.right_elbow = calculateAngle(keypoints[KP.RIGHT_SHOULDER], keypoints[KP.RIGHT_ELBOW], keypoints[10]);
  }

  if (
    validIdx(keypoints, KP.LEFT_SHOULDER) &&
    validIdx(keypoints, KP.RIGHT_SHOULDER) &&
    validIdx(keypoints, KP.LEFT_HIP) &&
    validIdx(keypoints, KP.RIGHT_HIP)
  ) {
    const ls = keypoints[KP.LEFT_SHOULDER];
    const rs = keypoints[KP.RIGHT_SHOULDER];
    const lh = keypoints[KP.LEFT_HIP];
    const rh = keypoints[KP.RIGHT_HIP];
    const shoulderMid: [number, number] = [(ls[0] + rs[0]) / 2, (ls[1] + rs[1]) / 2];
    const hipMid: [number, number] = [(lh[0] + rh[0]) / 2, (lh[1] + rh[1]) / 2];
    angles.spine = calculateVerticalAngle(shoulderMid, hipMid);
  }

  if (validIdx(keypoints, KP.LEFT_SHOULDER) && validIdx(keypoints, KP.RIGHT_SHOULDER)) {
    angles.shoulder_tilt =
      Math.abs(keypoints[KP.LEFT_SHOULDER][1] - keypoints[KP.RIGHT_SHOULDER][1]) * 100;
  }

  return angles;
}

export function mergeAngles(
  keypoints: Kp[],
  serverAngles?: Record<string, number>,
): Record<string, number> {
  return { ...calculateBodyAngles(keypoints), ...(serverAngles ?? {}) };
}

export interface AngleLabelPlacement {
  key: string;
  value: number;
  anchorX: number;
  anchorY: number;
  labelX: number;
  labelY: number;
}

function labelOffset(key: string, stackIndex: number): { dx: number; dy: number } {
  const stack = stackIndex * 22;
  if (key === 'spine') return { dx: 0, dy: -38 - stack };
  if (key === 'shoulder_tilt') return { dx: 0, dy: -50 - stack };
  if (key.startsWith('left_')) return { dx: -58, dy: -24 - stack };
  if (key.startsWith('right_')) return { dx: 58, dy: -24 - stack };
  return { dx: 0, dy: -28 - stack };
}

function clampLabelCenter(
  x: number,
  y: number,
  pillW: number,
  pillH: number,
  canvasW: number,
  canvasH: number,
): { x: number; y: number } {
  const halfW = pillW / 2;
  const halfH = pillH / 2;
  return {
    x: Math.max(halfW + 4, Math.min(x, canvasW - halfW - 4)),
    y: Math.max(halfH + 4, Math.min(y, canvasH - halfH - 4)),
  };
}

export function computeAnglePlacements(
  angles: Record<string, number>,
  keypoints: Kp[],
  w: number,
  h: number,
): AngleLabelPlacement[] {
  const raw: { key: string; value: number; anchorX: number; anchorY: number }[] = [];

  for (const [key, value] of Object.entries(angles)) {
    if (!(key in ANGLE_LABELS) || !Number.isFinite(value)) continue;

    const anchor = ANGLE_ANCHOR[key];
    if (anchor === undefined) continue;

    let anchorX: number | null = null;
    let anchorY: number | null = null;

    if (typeof anchor === 'number') {
      const kp = keypoints[anchor];
      if (!validKp(kp)) continue;
      anchorX = kp[0] * w;
      anchorY = kp[1] * h;
    } else if (anchor === 'mid_shoulder') {
      const ls = keypoints[KP.LEFT_SHOULDER];
      const rs = keypoints[KP.RIGHT_SHOULDER];
      if (!validKp(ls) || !validKp(rs)) continue;
      anchorX = ((ls[0] + rs[0]) / 2) * w;
      anchorY = ((ls[1] + rs[1]) / 2) * h;
    } else if (anchor === 'mid_spine') {
      const ls = keypoints[KP.LEFT_SHOULDER];
      const rs = keypoints[KP.RIGHT_SHOULDER];
      const lh = keypoints[KP.LEFT_HIP];
      const rh = keypoints[KP.RIGHT_HIP];
      if (!validKp(ls) || !validKp(rs) || !validKp(lh) || !validKp(rh)) continue;
      const sx = (ls[0] + rs[0]) / 2;
      const sy = (ls[1] + rs[1]) / 2;
      const hx = (lh[0] + rh[0]) / 2;
      const hy = (lh[1] + rh[1]) / 2;
      anchorX = ((sx + hx) / 2) * w;
      anchorY = ((sy + hy) / 2) * h;
    }

    if (anchorX === null || anchorY === null) continue;
    raw.push({ key, value, anchorX, anchorY });
  }

  const groups = new Map<string, typeof raw>();
  for (const item of raw) {
    const id = `${Math.round(item.anchorX)}:${Math.round(item.anchorY)}`;
    const list = groups.get(id) ?? [];
    list.push(item);
    groups.set(id, list);
  }

  const pillH = 22;
  const placements: AngleLabelPlacement[] = [];

  for (const items of groups.values()) {
    items.sort((a, b) => a.key.localeCompare(b.key));
    items.forEach((item, stackIndex) => {
      const { dx, dy } = labelOffset(item.key, stackIndex);
      const labelText = `${ANGLE_LABELS[item.key]} ${Math.round(item.value)}°`;
      const estW = labelText.length * 8 + 16;
      const { x: labelX, y: labelY } = clampLabelCenter(
        item.anchorX + dx,
        item.anchorY + dy,
        estW,
        pillH,
        w,
        h,
      );
      placements.push({
        key: item.key,
        value: item.value,
        anchorX: item.anchorX,
        anchorY: item.anchorY,
        labelX,
        labelY,
      });
    });
  }

  return placements;
}
